import React from 'react';
import { Coffee, CheckCircle2, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { BreakTableCard, BreakStatusPill, BreakAvatar, BreakEmptyState, BreakLoadingState } from '../hr/break/ui/BreakUi';
import { BreakStatusBadge, formatDuration, formatDateTimeShortET } from '../hr/break/breakUiHelpers';

/* IT-BREAK-MGMT-001 — read-only presentation (no force-end / edit actions by design) */

export const TeamStatusTable = ({ rows, loading }) => (
  <BreakTableCard testId="it-break-status-table">
    {loading ? <BreakLoadingState label="Loading team status…" testId="it-break-status-loading" /> : rows.length === 0 ? (
      <div className="p-3"><BreakEmptyState tone="neutral" icon={<Coffee className="h-5 w-5" />} title="No team members match" subtitle="Adjust the employee search or status filter." testId="it-break-status-empty" /></div>
    ) : (
      <Table className="tf-break-table">
        <TableHeader>
          <TableRow className="bm-thead">
            <TableHead>Employee</TableHead>
            <TableHead>Current Status</TableHead>
            <TableHead>Break Type</TableHead>
            <TableHead>Break Start</TableHead>
            <TableHead>Break End</TableHead>
            <TableHead className="text-right">Duration</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => {
            const b = r.activeBreak;
            const elapsed = b?.elapsed_minutes || 0; const cap = b?.max_duration_minutes || 0;
            const over = b && (b.is_over_limit || (cap > 0 && elapsed > cap));
            return (
              <TableRow key={r.id} className={`bm-row ${over ? 'bg-rose-50/60' : ''}`} style={{ animationDelay: `${i * 30}ms` }} data-testid={`it-break-member-${r.id}`} data-status={b ? 'on_break' : 'active'}>
                <TableCell className="font-medium text-slate-900"><span className="flex items-center gap-2.5 text-slate-900"><BreakAvatar name={r.name} tone={b ? 'emerald' : 'sky'} />{r.name}</span></TableCell>
                <TableCell>
                  {b ? <BreakStatusPill tone={over ? 'red' : 'amber'} dot testId={`it-break-member-${r.id}-status`}>{over ? `On Break · Over Cap +${Math.max(0, elapsed - cap)}m` : 'On Break'}</BreakStatusPill>
                    : <BreakStatusPill tone="green" dot testId={`it-break-member-${r.id}-status`}>Active</BreakStatusPill>}
                </TableCell>
                <TableCell className="text-[12.5px] text-slate-700">{b?.break_type_name || '—'}</TableCell>
                <TableCell className="text-xs text-slate-600">{b?.start_time ? formatDateTimeShortET(b.start_time) : '—'}</TableCell>
                <TableCell className="text-xs text-slate-600">{b ? <span className="text-amber-600 font-medium">In progress</span> : '—'}</TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {b ? <><span className={`font-semibold ${over ? 'text-rose-700' : 'text-emerald-700'}`}>{formatDuration(elapsed)}</span>{cap > 0 && <span className="text-slate-400"> / {formatDuration(cap)}</span>}</> : '—'}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    )}
  </BreakTableCard>
);

export const BreakHistoryTable = ({ breaks, loading, onAcknowledge = null }) => (
  <BreakTableCard testId="it-break-history-table">
    {loading ? <BreakLoadingState label="Loading break history…" testId="it-break-history-loading" /> : breaks.length === 0 ? (
      <div className="p-3"><BreakEmptyState tone="neutral" icon={<Coffee className="h-5 w-5" />} title="No breaks match these filters" subtitle="Adjust the employee, date range or status to see recorded breaks." testId="it-break-history-empty" /></div>
    ) : (
      <Table className="tf-break-table">
        <TableHeader>
          <TableRow className="bm-thead">
            <TableHead>Employee</TableHead>
            <TableHead>Break Start</TableHead>
            <TableHead>Break End</TableHead>
            <TableHead className="text-right">Duration</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Status</TableHead>
            {onAcknowledge && <TableHead className="text-right">Review</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {breaks.map((b, i) => (
            <TableRow key={b.id} className="bm-row" style={{ animationDelay: `${i * 25}ms` }} data-testid={`it-break-history-row-${b.id}`}>
              <TableCell className="font-medium text-slate-900"><span className="flex items-center gap-2.5 text-slate-900"><BreakAvatar name={b.user_name || b.user_id} tone="sky" />{b.user_name || b.user_id}</span></TableCell>
              <TableCell className="text-xs text-slate-700">{formatDateTimeShortET(b.start_time)}</TableCell>
              <TableCell className="text-xs text-slate-700">{b.end_time ? formatDateTimeShortET(b.end_time) : <span className="text-amber-600 font-medium">In progress</span>}</TableCell>
              <TableCell className="text-right font-mono text-xs">
                <span className={b.max_duration_minutes != null && b.duration_minutes > b.max_duration_minutes ? 'text-rose-700 font-semibold' : 'text-slate-800'}>{formatDuration(b.duration_minutes)}</span>
                {b.max_duration_minutes != null && <span className="text-slate-400"> / {formatDuration(b.max_duration_minutes)}</span>}
              </TableCell>
              <TableCell className="text-[12.5px] text-slate-700">{b.break_type_name || '—'}</TableCell>
              <TableCell><BreakStatusBadge status={b.status} flagged={b.flagged} waived={b.entitlement_waived} /></TableCell>
              {onAcknowledge && (
                <TableCell className="text-right">
                  {/* IT-BREAK-ACK-001: same TL acknowledgement control (AcknowledgeBreakModal) for flagged rows */}
                  {b.flagged ? (
                    <div className="flex flex-col items-end gap-0.5">
                      <Button variant={b.admin_acknowledged ? 'ghost' : 'outline'} size="sm" onClick={() => onAcknowledge(b)}
                        className={`bm-btn h-8 rounded-lg ${b.admin_acknowledged ? 'text-slate-600' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'}`}
                        data-testid={`it-ack-btn-${b.id}`}>
                        {b.admin_acknowledged ? <><FileText className="w-3.5 h-3.5 mr-1" /> View note</> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Acknowledge</>}
                      </Button>
                      {b.admin_acknowledged && (
                        <span className="text-[10.5px] text-emerald-700" data-testid={`it-ack-meta-${b.id}`}>
                          {b.admin_acknowledged_by_name || 'Reviewed'} · {formatDateTimeShortET(b.admin_acknowledged_at)}
                        </span>
                      )}
                    </div>
                  ) : <span className="text-[11px] text-slate-300">—</span>}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )}
  </BreakTableCard>
);
